import { useState, useEffect, useCallback, useRef, useContext } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { useLocation } from 'react-router-dom'
import CreateJs from 'preload-js'
import { Context } from '@/context'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Meta from '@/components/Meta'
import Hero from '@/components/Hero'
import CategoryDescription from '@/components/CategoryDescription'
import BlockTitle from '@/components/BlockTitle'
import Carousel from '@/components/Carousel'
import Footer from '@/components/Footer'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import actions from '@/actions'
import { getSlug } from '@/utils/path'
import style from './style'

const useStyles = createUseStyles(style)

const About = () => {
  const { getImageUrl, setPageAnimationReady, headerHeight } = useContext(Context)
  const classes = useStyles({ headerHeight })
  const [load, setLoad] = useState(false)
  const preload = useRef(new CreateJs.LoadQueue())
  const $root = useRef()
  const location = useLocation()
  const stub = useRef(getSlug(location.pathname))
  const gallery = useRef([])

  /*------------------------------
  Executes a callback on loading each image
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, mediaQueryKey } = useSelector((state) => ({
    current: state.page.current,
    page: state.page.pages[stub.current] || {},
    mediaQueryKey: state.mediaQuery.key,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const updatePageReady = useCallback((index) => dispatch(actions.loading.setReady(index)), [dispatch])
  const setSiteLoaded = useCallback(() => dispatch(actions.loading.setSiteLoaded()), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    updatePageReady(true)
    setSiteLoaded()
    setLoad(true)
  }

  /*------------------------------
  Preload Main Images: preloading only thre three first image
  ------------------------------*/
  const preloadMedia = () => {
    preload.current.on('complete', completePreload)
    const heroMedia = getImageUrl(page.featured_media, 'full', stub.current, 'pages')
    if (page.featured_media !== 0) preload.current.loadFile(heroMedia)
  }

  /*------------------------------
  Set Gallery
  ------------------------------*/
  const setGallery = () => {
    if (page.project_gallery_id) {
      const gal = page.project_gallery_id.split(',')
      const imgSize = 'full'
      const galleryItems = gal.map((i) => {
        // Controllo se esiste l'immagine su media_post
        if (page.media_post[i]) {
          const { width, height, caption } = page.media_post[i]
          const src = getImageUrl(i, imgSize, stub.current, 'pages')
          const ratio = height / width

          return {
            src,
            width,
            height,
            caption,
            ratio,
          }
        }
        return null
      })

      gallery.current = galleryItems[0] ? galleryItems : []
    }
  }

  /*------------------------------
  Initialize (API Calls)
  ------------------------------*/
  const initialize = () => {
    if (current !== stub.current) setCurrentPage(stub.current, 'pages')
    if (Object.keys(page).length === 0) getPage(stub.current)
  }

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Activate Page Animation
  ------------------------------*/
  useEffect(() => {
    if (load) setPageAnimationReady(true)
  }, [load])

  /*------------------------------
  If there are products in category complete the preload
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) {
      preloadMedia()
      setGallery()
    }
  }, [page])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta
        title={page.yoast_meta.find((y) => y.property?.includes('title')).content || page.title.rendered}
        meta={page.yoast_meta}
        schema={page.yoast_json_ld}
      />
    )
  }

  /*------------------------------
  Render Hero
  ------------------------------*/
  const renderHero = () => {
    return page.featured_media && (
      <Hero
        img={getImageUrl(page.featured_media, 'full', stub.current, 'pages')}
        heightPercent={mediaQueryKey > 2 ? '30%' : '56.25%'}
      />
    )
  }

  /*------------------------------
  Render Category Description
  ------------------------------*/
  const renderDescription = () => {
    return (
      <div className={`${classes.wrapper} ${classes.description}`}>
        <CategoryDescription
          title={page.title.rendered}
          info={page.content.rendered}
        />
      </div>
    )
  }

  /*------------------------------
  Render Text
  ------------------------------*/
  const renderText = () => {
    return (
      <div className={`${classes.wrapper} ${classes.textWrapper}`}>
        <div
          className={classes.text}
          dangerouslySetInnerHTML={{ __html: page.banner_single_text }}
        />
      </div>
    )
  }

  /*------------------------------
  Render Story
  ------------------------------*/
  const renderStory = () => {
    const list = page.story_list.map((item, index) => {
      const parallax = (mediaQueryKey > 1 && index % 2 === 0) ? {} : { 'data-scroll-speed': 1 }

      return ( // eslint-disable-next-line
        <div className={classes.storyItem} {...parallax} key={index.toString()}>
          <div>
            <div
              className={classes.storyItemTitle}
              dangerouslySetInnerHTML={{ __html: item.title }}
            />
            <div
              className={classes.storyItemText}
              dangerouslySetInnerHTML={{ __html: item.story_list_text }}
            />
          </div>
        </div>
      )
    })

    return (
      <div className={`${classes.story} grid`}>
        <div className={classes.storyIntro}>
          <BlockTitle
            data={{
              preTitle: page.story_pretitle,
              title: page.story_title,
              paragraph: page.story_text,
            }}
          />
        </div>
        <div className={classes.storyList}>
          {list}
        </div>
      </div>
    )
  }

  /*------------------------------
  Render Gallery
  ------------------------------*/
  const renderGalleryProduct = () => {
    return gallery.current.length > 1 && (
      <div className={`${classes.wrapper} ${classes.gallery}`}>
        <Carousel
          imagesList={gallery.current}
          parallax
          dots
          arrows
        />
      </div>
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => <Footer storeLocator />

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderHero()}
        {renderDescription()}
        {renderText()}
        {renderStory()}
        {renderGalleryProduct()}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageAbout ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default About
